<script setup>
import { ref, onMounted, nextTick } from "vue"
import { useRoute } from "vue-router"
import { SearchIcon } from "@zhuowenli/vue-feather-icons"
import Layout from "../../layouts/main.vue"
import PersianDate from "persian-date"

import store from "../../state/store"

const route = useRoute()
const userId = route.params.userId

const messagesData = ref([])
const replyMessage = ref({
  user_id: userId,
  message: "",
})
const loading = ref(false)
const errors = ref({})
const userInfo = ref()

// To save the selected image and show its preview
const selectedImage = ref(null)
const previewImage = ref(null)

const isModalOpen = ref(false)
const selectedImageUrl = ref(null)

const scrollToBottom = (id) => {
  const simpleBar = document
    .getElementById(id)
    .querySelector("#chat-conversation .simplebar-content-wrapper")
    ? document
        .getElementById(id)
        .querySelector("#chat-conversation .simplebar-content-wrapper")
    : ""

  const offsetHeight = document.getElementsByClassName(
    "chat-conversation-list"
  )[0]
    ? document
        .getElementById(id)
        .getElementsByClassName("chat-conversation-list")[0].scrollHeight -
      window.innerHeight +
      600
    : 0

  if (offsetHeight) {
    simpleBar.scrollTo({
      top: offsetHeight,
      behavior: "smooth",
    })
  }
}

const fetchData = async () => {
  try {
    const { data } = await store.dispatch("getUserMessages", userId)
    messagesData.value = {
      ...data.data,
    }

    const userData = await store.dispatch("getUser", userId)
    userInfo.value = {
      ...userData.data.data,
    }
  } catch (error) {
    console.error("خطا در دریافت داده‌ها:", error)
  }
}

onMounted(async () => {
  await fetchData()

  const currentChatId = "users-chat"

  await nextTick()
  scrollToBottom(currentChatId)
})

const toPersianDateTime = (date) => {
  const [datePart, timePart] = date.split(" ")
  const [year, month, day] = datePart.split("-")
  const [hour, minute] = timePart.split(":")
  const gregorianDate = new Date(year, month - 1, day, hour, minute)
  const persianDate = new PersianDate(gregorianDate)
  return persianDate.format("YYYY/MM/DD HH:mm")
}

// Function to select image
const triggerImageUpload = () => {
  document.querySelector('input[type="file"]').click()
}

// Image upload management and preview display
const handleImageUpload = (event) => {
  const file = event.target.files[0]
  if (file) {
    selectedImage.value = file
    previewImage.value = URL.createObjectURL(file)
  }
}

// Deselect the image and return to the initial state
const cancelImage = () => {
  selectedImage.value = null
  previewImage.value = null
}

// Function to open the modal and display the image
const openImagePreview = (imageUrl) => {
  selectedImageUrl.value = imageUrl
  isModalOpen.value = true
}

// Function to close the modal
const closeImagePreview = () => {
  isModalOpen.value = false
  selectedImageUrl.value = null
}

const onSubmit = () => {
  // Checking that at least one message or image is selected
  if (!replyMessage.value.message && !selectedImage.value) {
    errors.value = {
      message: ["لطفا یک پیام وارد کنید یا تصویری انتخاب کنید."],
    }
    return
  }

  loading.value = true
  const formData = new FormData()

  formData.append("user_id", replyMessage.value.user_id)

  // If an image was selected, only the image will be sent
  if (selectedImage.value) {
    formData.append("image", selectedImage.value)
  } else {
    formData.append("message", replyMessage.value.message)
  }

  store
    .dispatch("replyToMessage", formData)
    .then((response) => {
      loading.value = false
      if (response.status === 201) {
        window.location.reload()

        replyMessage.value.message = ""
        selectedImage.value = null
        previewImage.value = null
        errors.value = null
      }
    })
    .catch((err) => {
      loading.value = false
      errors.value = err.response.data.errors
    })
}
</script>

<template>
  <Layout>
    <b-overlay
      variant="white"
      opacity="0.75"
      blur="2px"
      :show="loading"
      spinner-variant="success"
      rounded="sm"
    >
      <div class="chat-wrapper d-flex gap-1 mx-n4 mt-n4 p-1">
        <div class="user-chat w-100 overflow-hidden">
          <div class="chat-content d-flex">
            <div class="w-100 overflow-hidden position-relative">
              <div class="position-relative">
                <div class="p-3 user-chat-topbar">
                  <b-row class="align-items-center">
                    <b-col sm="4" cols="8">
                      <div class="d-flex align-items-center">
                        <div class="flex-grow-1 overflow-hidden">
                          <div class="d-flex align-items-center">
                            <div class="flex-grow-1 overflow-hidden">
                              <h5 class="text-truncate mb-0 fs-16">
                                <b-link
                                  class="text-reset username"
                                  data-bs-toggle="offcanvas"
                                  href="#userProfileCanvasExample"
                                  aria-controls="userProfileCanvasExample"
                                  >
                                  {{
                                    
                                    userInfo ? (userInfo.name === null ? '-' : userInfo.name) + " " + (userInfo.surname === null ? '-' : userInfo.surname) : 'اطلاعاتی یافت نشد!'
                                      
                                  }}
                                </b-link>
                              </h5>
                              <p
                                class="text-truncate text-muted fs-14 mb-0 userStatus"
                              >
                                <small>کاربر</small>
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </b-col>
                    <b-col sm="8" cols="4">
                      <ul class="list-inline user-chat-nav text-end mb-0">
                        <li class="list-inline-item m-0">
                          <div class="dropdown">
                            <button
                              class="btn btn-ghost-secondary btn-icon shadow-none"
                              type="button"
                              data-bs-toggle="dropdown"
                              aria-haspopup="true"
                              aria-expanded="false"
                            >
                              <search-icon class="icon-sm"></search-icon>
                            </button>
                            <div
                              class="dropdown-menu p-0 dropdown-menu-end dropdown-menu-lg"
                            >
                              <div class="p-2">
                                <div class="search-box">
                                  <input
                                    type="text"
                                    class="form-control bg-light border-light"
                                    placeholder="جستجو..."
                                    onkeyup="searchMessages()"
                                    id="searchMessage"
                                  />
                                  <i class="ri-search-2-line search-icon"></i>
                                </div>
                              </div>
                            </div>
                          </div>
                        </li>
                      </ul>
                    </b-col>
                  </b-row>
                </div>

                <div class="position-relative" id="users-chat">
                  <div
                    class="chat-conversation p-3 p-lg-4"
                    id="chat-conversation"
                    data-simplebar
                    ref="current"
                  >
                    <ul class="list-unstyled chat-conversation-list">
                      <li
                        v-for="data of messagesData"
                        :key="data.id"
                        :class="{
                          right: `${data.senderType}` === 'support',
                          left: `${data.senderType}` === 'user',
                        }"
                        class="chat-list"
                      >
                        <div class="conversation-list">
                          <div class="user-chat-content">
                            <div class="ctext-wrap">
                              <div class="ctext-wrap-content">
                                <p
                                  v-if="data.messageText"
                                  class="mb-0 ctext-content"
                                >
                                  {{ data.messageText }}
                                </p>

                                <img
                                  v-if="data.image_url"
                                  :src="data.image_url"
                                  alt="uploaded image"
                                  class="chat-image-preview mt-2"
                                  style="
                                    max-width: 200px;
                                    max-height: 200px;
                                    border-radius: 8px;
                                    cursor: pointer;
                                  "
                                  @click="openImagePreview(data.image_url)"
                                />

                                <p
                                  v-if="data.support"
                                  class="fw-bold mt-3 mb-0 text-warning"
                                >
                                  <small>
                                    {{ data.support.first_name }}
                                    {{ data.support.last_name }}</small
                                  >
                                </p>
                              </div>

                              <div class="conversation-name mx-3">
                                <small class="text-muted time">{{
                                  toPersianDateTime(data.datetime)
                                }}</small>
                                <span
                                  v-if="data.isRead"
                                  class="text-success check-message-icon"
                                  ><i
                                    class="ri-check-double-line align-bottom"
                                  ></i
                                ></span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </li>
                    </ul>
                  </div>
                </div>

                <div class="chat-input-section p-3 p-lg-4">
                  <form @submit.prevent="onSubmit">
                    <b-row class="g-0 align-items-center">
                      <b-col>
                        <div
                          v-if="selectedImage"
                          class="image-preview-container"
                        >
                          <!-- Show image preview -->
                          <div class="image-preview-card">
                            <img
                              :src="previewImage"
                              alt="Preview"
                              class="img-thumbnail image-preview-img"
                            />
                          </div>

                          <!-- Cancel button -->

                          <b-button
                            variant="danger"
                            @click="cancelImage"
                            class="btn-icon waves-effect waves-light ms-2"
                            ><i class="ri-delete-bin-5-line"></i
                          ></b-button>
                        </div>

                        <!-- Display message input only if no image is selected -->
                        <div v-else>
                          <input
                            type="text"
                            v-model="replyMessage.message"
                            class="form-control chat-input bg-light border-light"
                            placeholder="پیام خود را تایپ کنید..."
                            :class="{
                              'is-invalid': errors && errors.message,
                            }"
                            :disabled="selectedImage !== null"
                          />
                          <div v-if="errors" class="invalid-feedback">
                            <span v-if="errors.message">{{
                              errors.message[0]
                            }}</span>
                          </div>
                        </div>
                      </b-col>

                      <!-- Image selection button -->
                      <b-col cols="auto">
                        <div
                          v-if="!selectedImage"
                          class="chat-input-links ms-2"
                        >
                          <input
                            type="file"
                            ref="imageInput"
                            @change="handleImageUpload"
                            class="d-none"
                            accept="image/*"
                          />
                          <button
                            type="button"
                            @click="triggerImageUpload"
                            class="btn btn-icon btn-ghost-secondary"
                          >
                            <i class="ri-image-add-fill fs-3"></i>
                          </button>
                        </div>
                      </b-col>

                      <b-col cols="auto">
                        <div class="chat-input-links ms-2">
                          <div class="links-list-item">
                            <b-button
                              variant="primary"
                              type="submit"
                              class="chat-send shadow"
                            >
                              <i class="ri-send-plane-2-fill align-bottom"></i>
                            </b-button>
                          </div>
                        </div>
                      </b-col>
                    </b-row>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </b-overlay>
  </Layout>

  <!-- Image preview modal -->
  <div
    v-if="isModalOpen"
    class="image-modal-overlay"
    @click="closeImagePreview"
  >
    <div class="image-modal-content">
      <img :src="selectedImageUrl" alt="Preview" class="modal-image" />
    </div>
  </div>
</template>

<style>
@media (max-width: 991.98px) {
  .user-chat {
    position: relative !important;
    visibility: visible !important;
    transform: translateX(0) !important;
  }
}

.ri-send-plane-2-fill {
  transform: rotate(180deg);
}

.image-preview-container {
  display: flex;
  align-items: center;
}

.image-preview-card {
  display: inline-block;
  max-width: 100px;
  max-height: 100px;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #ddd;
  margin-right: 10px;
}

.image-preview-img {
  width: 100%;
  height: auto;
  object-fit: cover;
  border-radius: 8px;
}

.btn-icon {
  font-size: 20px;
}

.invalid-feedback {
  display: block !important;
}

/* modal and preview style */
.image-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.image-modal-content {
  max-width: 90%;
  max-height: 90%;
}

.modal-image {
  max-width: 100%;
  max-height: 100%;
  border-radius: 8px;
  object-fit: contain;
  cursor: pointer;
}

/* Small preview style of the image in the list */
.chat-image-preview {
  transition: transform 0.3s ease;
}

.chat-image-preview:hover {
  transform: scale(1.05);
}
</style>
